import React, { useMemo } from 'react';
import { daysAgoKey } from '@coparent/shared/src/services/adminMetricsService';

// Trailing-window buckets, in UTC days to match the rest of the dashboard.
// `days` is inclusive of today, so a 7-day window is today + the prior 6.
const WINDOWS = [
  { key: '1d', label: '24h', days: 1 },
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
];

function formatNumber(value) {
  if (value == null) return '—';
  if (Math.abs(value) >= 10_000) return `${(value / 1000).toFixed(1)}k`;
  return value.toLocaleString();
}

/**
 * Replaces the "Landing views" tile in the top strip. Sums per-day
 * `accountDeletions/{YYYY-MM-DD}.count` docs into 1 / 7 / 30-day trailing
 * windows. Renders 0s (not an empty state) until the first deletion is
 * recorded after the onUserDeleteMetrics deploy — there is no backfill.
 */
const DeletedAccountsCard = ({ deletions = [] }) => {
  const totals = useMemo(() => {
    // cutoffs[i] is the oldest doc id (inclusive) that still counts for that
    // window. daysAgoKey(1) === yesterday, so a 1-day window keeps today only.
    const cutoffs = WINDOWS.map((w) => daysAgoKey(w.days - 1));
    const sums = WINDOWS.map(() => 0);
    for (const d of deletions) {
      const id = d.id || '';
      const count = d.count || 0;
      cutoffs.forEach((cutoff, i) => {
        if (id >= cutoff) sums[i] += count;
      });
    }
    return sums;
  }, [deletions]);

  return (
    <div className="card p-5 flex flex-col gap-2 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <p
          className="text-xs font-semibold uppercase tracking-wide text-ink-400 truncate cursor-help"
          title="Accounts deleted"
        >
          Accounts deleted
        </p>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-blush-700 bg-blush/15">
          trailing
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        {WINDOWS.map((w, i) => (
          <div key={w.key} className="flex flex-col gap-0.5 min-w-0">
            <span className="font-display text-2xl font-semibold text-ink-900 leading-none">
              {formatNumber(totals[i])}
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wide text-ink-400 truncate">
              {w.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeletedAccountsCard;
